import type { IdTokenResult, User } from "firebase/auth";
import type { MfaEnrollmentFactor } from "./mfaEnrollment";

export type MfaComplianceVerdict =
  | { status: "compliant" }
  | { status: "needs_enrollment"; returnTo: string }
  | { status: "needs_fresh_login"; returnTo: string }
  | { status: "unavailable" }
  | { status: "impersonation_blocked" };

/** Session MFA lives on IdTokenResult.signInSecondFactor, not claims.signInSecondFactor. */
export function sessionUsedSecondFactor(tokenResult: Pick<IdTokenResult, "signInSecondFactor" | "claims">): boolean {
  if (tokenResult.signInSecondFactor) {
    return true;
  }
  const firebaseClaims = tokenResult.claims.firebase;
  if (!firebaseClaims || typeof firebaseClaims !== "object") {
    return false;
  }
  return Boolean((firebaseClaims as { sign_in_second_factor?: unknown }).sign_in_second_factor);
}

export function isNonCompliantMfaVerdict(
  verdict: MfaComplianceVerdict,
): verdict is Extract<MfaComplianceVerdict, { status: "needs_enrollment" | "needs_fresh_login" }> {
  return verdict.status === "needs_enrollment" || verdict.status === "needs_fresh_login";
}

export function withPreservedMfaReturnTo(
  verdict: MfaComplianceVerdict,
  preservedReturnTo: string | null,
): MfaComplianceVerdict {
  if (!preservedReturnTo || !isNonCompliantMfaVerdict(verdict)) {
    return verdict;
  }
  return { ...verdict, returnTo: preservedReturnTo };
}

export async function evaluateMfaCompliance({
  user,
  mfaRequired,
  getMfaEnrollmentFactors,
  getReturnTo,
}: {
  user: User;
  mfaRequired: boolean;
  getMfaEnrollmentFactors: () => Promise<MfaEnrollmentFactor[]>;
  getReturnTo: () => string;
}): Promise<MfaComplianceVerdict> {
  if (!mfaRequired) {
    return { status: "compliant" };
  }

  try {
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult.claims.impersonation === true) return { status: "impersonation_blocked" };
    if (sessionUsedSecondFactor(tokenResult)) return { status: "compliant" };
    const factors = await getMfaEnrollmentFactors();
    const returnTo = getReturnTo();

    if (factors.length === 0) {
      return { status: "needs_enrollment", returnTo };
    }
    if (!sessionUsedSecondFactor(tokenResult)) {
      return { status: "needs_fresh_login", returnTo };
    }
    return { status: "compliant" };
  } catch {
    return { status: "unavailable" };
  }
}

export function organizationRequiresMfa(organization?: { mfa_policy?: string; mfa_state?: string } | null): boolean {
  return organization?.mfa_state === "ENFORCED" || (organization?.mfa_policy === "REQUIRED" && !organization.mfa_state);
}

export function membershipsRequireMfa(
  permissions?: {
    superadmin_mfa_required?: boolean;
    organizations?: { mfa_policy?: string; mfa_state?: string }[];
  } | null,
): boolean {
  return Boolean(permissions?.superadmin_mfa_required || permissions?.organizations?.some(organizationRequiresMfa));
}
